import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  FiBookOpen,
  FiCalendar,
  FiChevronRight,
  FiMapPin,
  FiSearch,
  FiUsers,
} from "react-icons/fi";

const weekdays = [
  { value: "mon", label: "Thứ 2" },
  { value: "tue", label: "Thứ 3" },
  { value: "wed", label: "Thứ 4" },
  { value: "thu", label: "Thứ 5" },
  { value: "fri", label: "Thứ 6" },
  { value: "sat", label: "Thứ 7" },
  { value: "sun", label: "CN" },
];

function displayText(value, fallback = "—") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.vi || value.en || fallback;
  return String(value);
}

function renderScheduleShort(schedule = []) {
  if (!Array.isArray(schedule) || schedule.length === 0) return "Chưa có lịch";

  const labelMap = weekdays.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});

  return schedule
    .filter((item) => Array.isArray(item.days) && item.days.length && item.startTime && item.endTime)
    .map((item) => `${item.days.map((d) => labelMap[d] || d).join(", ")} · ${item.startTime}-${item.endTime}`)
    .join(" | ") || "Chưa có lịch";
}

function normalizeRoles(user) {
  if (Array.isArray(user?.roles)) return user.roles;
  if (user?.role) return [user.role];
  return [];
}

export default function TeacherClassesList() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const roles = normalizeRoles(user);
  const canViewAll = roles.includes("admin") || roles.includes("editor");

  useEffect(() => {
    if (authLoading || !user?.uid) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const [progSnap, classList] = await Promise.all([
          getDocs(collection(db, "programs")),
          fetchTeacherClasses(user.uid, canViewAll),
        ]);

        if (cancelled) return;
        setPrograms(progSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setClasses(classList);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(err?.message || "Không thể tải danh sách lớp học.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, authLoading, canViewAll]);

  const fetchTeacherClasses = async (userId, viewAll) => {
    const classSnaps = await getDocs(collection(db, "classes"));

    const classesWithCount = await Promise.all(
      classSnaps.docs.map(async (classDoc) => {
        if (!viewAll) {
          const teacherSnap = await getDoc(doc(db, "classes", classDoc.id, "teachers", userId));
          if (!teacherSnap.exists()) return null;
        }

        let studentCount = 0;
        try {
          const qCount = query(
            collection(db, "classes", classDoc.id, "enrollments"),
            where("status", "in", ["active", "enrolled"])
          );
          const agg = await getCountFromServer(qCount);
          studentCount = agg.data().count || 0;
        } catch {
          const enrollSnap = await getDocs(collection(db, "classes", classDoc.id, "enrollments"));
          studentCount = enrollSnap.size;
        }

        return { id: classDoc.id, ...classDoc.data(), studentCount };
      })
    );

    return classesWithCount
      .filter(Boolean)
      .sort((a, b) => displayText(a.name).localeCompare(displayText(b.name), "vi"));
  };

  const programMap = useMemo(() => {
    return Object.fromEntries((programs || []).map((p) => [p.id, p]));
  }, [programs]);

  const filtered = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    return classes.filter((cls) => {
      const name = displayText(cls.name, "").toLowerCase();
      const program = programMap[cls.programId];
      const programName = displayText(program?.name || program?.level, "").toLowerCase();
      const okSearch = !keyword || `${name} ${programName}`.includes(keyword);
      const okStatus = !statusFilter || cls.status === statusFilter;
      return okSearch && okStatus;
    });
  }, [classes, programMap, searchTerm, statusFilter]);

  const stats = useMemo(
    () => ({
      total: classes.length,
      active: classes.filter((c) => c.status === "active").length,
      students: classes.reduce((sum, c) => sum + Number(c.studentCount || 0), 0),
    }),
    [classes]
  );

  if (authLoading) return <div className="p-8 text-center text-slate-500">Đang xác thực...</div>;
  if (!user) return <div className="p-8 text-center text-red-600">Bạn chưa đăng nhập.</div>;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lớp học của tôi</h1>
          <p className="mt-1 text-slate-600">
            Quản lý lớp phụ trách, lịch học, học viên, điểm danh và bài tập/kiểm tra.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl border bg-white px-4 py-3 text-center shadow-sm">
            <div className="text-xl font-bold text-blue-700">{stats.total}</div>
            <div className="text-xs text-slate-500">Tổng lớp</div>
          </div>
          <div className="rounded-xl border bg-white px-4 py-3 text-center shadow-sm">
            <div className="text-xl font-bold text-emerald-600">{stats.active}</div>
            <div className="text-xs text-slate-500">Đang học</div>
          </div>
          <div className="rounded-xl border bg-white px-4 py-3 text-center shadow-sm">
            <div className="text-xl font-bold text-amber-600">{stats.students}</div>
            <div className="text-xs text-slate-500">Học viên</div>
          </div>
        </div>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <section className="rounded-2xl border bg-white p-4 md:p-5 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="relative w-full md:w-96">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Tìm theo tên lớp hoặc chương trình..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-xl border bg-white py-2 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-xl border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/30"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="active">Đang hoạt động</option>
            <option value="inactive">Ngừng hoạt động</option>
          </select>
        </div>

        {loading ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-slate-500">Đang tải lớp học...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center text-slate-500">Không có lớp nào phù hợp.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map((cls) => {
              const program = programMap[cls.programId];
              const programName = displayText(program?.name || program?.level, "Chưa chọn chương trình");
              const active = cls.status === "active";

              return (
                <button
                  type="button"
                  key={cls.id}
                  onClick={() => navigate(`/teacher/classes/${cls.id}`)}
                  className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-200 hover:bg-blue-50/40 hover:shadow-md"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                            active
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 bg-slate-100 text-slate-600"
                          }`}
                        >
                          {active ? "Đang hoạt động" : "Ngừng hoạt động"}
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                          <FiBookOpen /> {programName}
                        </span>
                      </div>

                      <h2 className="mt-2 text-lg font-bold text-slate-900 line-clamp-1">
                        {displayText(cls.name, "Lớp học")}
                      </h2>

                      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> {renderScheduleShort(cls.schedule)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <FiMapPin /> {cls.room || "Chưa có phòng"}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <FiUsers /> {cls.studentCount || 0}{cls.maxStudents ? ` / ${cls.maxStudents}` : ""} học viên
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 lg:w-36 lg:justify-end text-blue-700 font-semibold">
                      <span>Vào lớp</span>
                      <FiChevronRight className="text-xl" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
